import React, { memo, useCallback, useEffect } from 'react'

import { bool, func, number, shape, string } from 'prop-types'

import Modal from 'Pods/Shared/Modal/Modal'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { setImageLightboxOpen } from 'Pods/ThreeDStudio/Store/actions/ui'

import Actions from '../Actions/Actions'

// Expanded view of one generated image.
//
// Generate 3D, Edit and Regenerate all move the user elsewhere - the panel closes, the
// Edit tab takes over, or a fresh run starts whose progress is drawn in the conversation
// - so each dismisses this first. Download is deliberately left out of that: it acts on
// the image in place, it opens the paid-feature upsell for users without the
// entitlement, and its in-flight and failure feedback only makes sense next to the image
// it came from.
function Lightbox({ result, isGenerating, onApply, onRegenerate, onEdit, onClose: handleClose }) {
  const t = useTranslation()

  // The modal is portalled to the body, so the panel's outside-press dismissal would
  // otherwise fire on every press in here and close the panel underneath. Flagged for
  // the lifetime of the component so the flag cannot outlive it - if the panel goes for
  // any other reason, this unmounts with it and clears itself.

  useEffect(() => {
    setImageLightboxOpen(true)

    return () => setImageLightboxOpen(false)
  }, [])

  const handleApply = useCallback(() => {
    handleClose()
    onApply()
  }, [handleClose, onApply])

  const handleRegenerate = useCallback(() => {
    handleClose()
    onRegenerate()
  }, [handleClose, onRegenerate])

  const handleEdit = useCallback(() => {
    handleClose()
    onEdit()
  }, [handleClose, onEdit])

  return (
    <Modal
      width="large"
      ariaLabel={t('imageAssistantPanel.createTab.result.lightbox')}
      customClass="image-assistant-lightbox"
      onClose={handleClose}
    >
      <img className="image-assistant-lightbox__image" src={result.url} alt={result.alt} />
      <Actions
        result={result}
        isGenerating={isGenerating}
        className="image-assistant-lightbox__actions"
        onApply={handleApply}
        onRegenerate={handleRegenerate}
        onEdit={handleEdit}
      />
    </Modal>
  )
}

Lightbox.propTypes = {
  result: shape({ id: number, url: string, alt: string }).isRequired,
  isGenerating: bool.isRequired,
  onApply: func.isRequired,
  onRegenerate: func.isRequired,
  onEdit: func.isRequired,
  onClose: func.isRequired,
}

export default memo(Lightbox)
